import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Calendar, Heart, MessageCircle, Settings, LogOut,
  ChevronRight, Clock, MapPin, Star, Building2, Check, X, Lock, Camera, Trash2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getBookings, cancelBooking, updateProfile, changePassword, getSavedClinics, unsaveClinic, getClinicImage, postReview, uploadAvatar, deleteAvatar } from '../lib/api';

const PatientProfilePage = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn, logout, updateUser, loading: authLoading } = useAuth();
  const isLoggingOut = useRef(false);
  const [activeTab, setActiveTab] = useState('bookings');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [savedClinics, setSavedClinics] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileInputRef = useRef(null);
  // Review modal state
  const [reviewModal, setReviewModal] = useState(null); // { bookingId, clinicName }
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewedBookingIds, setReviewedBookingIds] = useState(new Set());

  // Stable fetch function — called on mount and on tab focus
  const fetchBookings = useCallback(() => {
    setBookingsLoading(true);
    getBookings()
      .then((res) => setBookings(res.data.data || []))
      .catch(() => setBookings([]))
      .finally(() => setBookingsLoading(false));
  }, []);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize from localStorage
    if (!isLoggedIn && !isLoggingOut.current) {
      navigate('/login');
      return;
    }
    setEditForm({ name: user?.name || '', email: user?.email || '' });

    // Fetch real bookings
    fetchBookings();

    // Fetch saved clinics
    setSavedLoading(true);
    getSavedClinics()
      .then((res) => setSavedClinics(res.data?.data || []))
      .catch(() => setSavedClinics([]))
      .finally(() => setSavedLoading(false));
  }, [isLoggedIn, navigate, user, authLoading, fetchBookings]);

  // Re-fetch bookings when patient switches back to this tab
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchBookings();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchBookings]);

  const handleLogout = () => {
    isLoggingOut.current = true;
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSaveProfile = async () => {
    try {
      const { data } = await updateProfile({ name: editForm.name, email: editForm.email });
      // Sync auth state from the API-confirmed response
      updateUser(data?.data || editForm);
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const { data } = await uploadAvatar(file);
      updateUser({ avatar: data?.data?.avatar || data?.data?.avatarUrl || null });
      toast.success('Profile photo updated');
    } catch {
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setAvatarUploading(false);
      if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    setAvatarUploading(true);
    try {
      await deleteAvatar();
      updateUser({ avatar: null, avatarUrl: null });
      toast.success('Profile photo removed');
    } catch {
      toast.error('Failed to remove photo. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password changed successfully');
      setIsChangingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to change password';
      toast.error(msg);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await cancelBooking(bookingId);
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: 'cancelled' } : b))
      );
      toast.success('Booking cancelled');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to cancel booking';
      toast.error(msg);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) { toast.error('Please write a review comment'); return; }
    setReviewSubmitting(true);
    try {
      await postReview({ bookingId: reviewModal.bookingId, rating: reviewRating, comment: reviewText });
      setReviewedBookingIds((prev) => new Set([...prev, reviewModal.bookingId]));
      toast.success('Review submitted!');
      setReviewModal(null);
      setReviewText('');
      setReviewRating(5);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to submit review';
      toast.error(msg);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleUnsaveClinic = async (clinicId) => {
    try {
      await unsaveClinic(clinicId);
      setSavedClinics((prev) => prev.filter((c) => (c.clinicId || c.id) !== clinicId));
      toast.success('Clinic removed from saved');
    } catch {
      toast.error('Failed to remove saved clinic');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      confirmed: <span className="px-2.5 py-1 bg-[#2ECC71]/10 text-[#2ECC71] text-xs font-semibold rounded-full">Confirmed</span>,
      pending:   <span className="px-2.5 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">Awaiting Clinic Confirmation</span>,
      completed: <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">Completed</span>,
      cancelled: <span className="px-2.5 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">Cancelled</span>,
    };
    return map[status?.toLowerCase()] || <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">{status}</span>;
  };

  if (!user) return null;

  const tabs = [
    { id: 'bookings', label: 'My Bookings', icon: Calendar, count: bookings.length },
    { id: 'saved', label: 'Saved', icon: Heart, count: savedClinics.length },
    { id: 'messages', label: 'Messages', icon: MessageCircle, count: 0 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#1FA7A1]/10 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-[#1FA7A1]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    {user.name || 'Welcome!'}
                  </h1>
                  <p className="text-gray-500">{user.email}</p>
                  {user.phone && <p className="text-gray-400 text-sm">{user.phone}</p>}
                </div>
              </div>
              <Button variant="ghost" onClick={handleLogout} className="text-gray-500 hover:text-red-500">
                <LogOut className="h-5 w-5 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="flex border-b overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 py-4 px-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-[#1FA7A1] border-b-2 border-[#1FA7A1]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.count > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Bookings Tab */}
              {activeTab === 'bookings' && (
                <div className="space-y-4" data-testid="bookings-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-gray-900">Your Bookings</h2>
                    <Link to="/treatments">
                      <Button size="sm" className="bg-[#1FA7A1] hover:bg-[#178f8a]">Request Treatment</Button>
                    </Link>
                  </div>

                  {bookingsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1FA7A1] border-t-transparent mx-auto"></div>
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No bookings yet</p>
                      <Link to="/treatments"><Button>Request Treatment</Button></Link>
                    </div>
                  ) : (
                    bookings.map((booking) => (
                      <div key={booking.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-8 w-8 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-900">
                                  {booking.clinic?.name || 'Clinic'}
                                </h3>
                                <p className="text-[#1FA7A1] text-sm font-medium">
                                  {booking.treatment?.name || booking.treatmentId || 'Treatment'}
                                </p>
                              </div>
                              {getStatusBadge(booking.status)}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {booking.appointmentDate
                                  ? new Date(booking.appointmentDate).toLocaleDateString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric',
                                    })
                                  : 'Date TBD'}
                              </div>
                              {booking.appointmentTime && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {booking.appointmentTime}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <Link
                                to={`/bookings/${booking.id}`}
                                className="text-xs text-[#1FA7A1] hover:underline font-medium"
                                data-testid={`booking-detail-${booking.id}`}
                              >
                                View Details →
                              </Link>
                              <div className="flex items-center gap-2">
                                {['pending', 'confirmed'].includes(booking.status?.toLowerCase()) && (
                                  <button
                                    onClick={() => handleCancelBooking(booking.id)}
                                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                                    data-testid={`cancel-booking-${booking.id}`}
                                  >
                                    Cancel
                                  </button>
                                )}
                                {booking.status?.toLowerCase() === 'completed' && (
                                  reviewedBookingIds.has(booking.id) ? (
                                    <span className="text-xs text-gray-400 font-medium" data-testid={`reviewed-${booking.id}`}>Reviewed ✓</span>
                                  ) : (
                                    <button
                                      onClick={() => setReviewModal({ bookingId: booking.id, clinicName: booking.clinic?.name || 'Clinic' })}
                                      className="text-xs bg-[#1FA7A1] text-white rounded-lg px-3 py-1.5 hover:bg-[#178a85]"
                                      data-testid={`write-review-${booking.id}`}
                                    >
                                      Write Review
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Saved Clinics Tab */}
              {activeTab === 'saved' && (
                <div className="space-y-4" data-testid="saved-tab">
                  <h2 className="font-bold text-gray-900 mb-4">Saved Clinics</h2>
                  {savedLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1FA7A1] border-t-transparent mx-auto"></div>
                    </div>
                  ) : savedClinics.length === 0 ? (
                    <div className="text-center py-12">
                      <Heart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No saved treatments yet</p>
                      <Link to="/treatments"><Button>Explore Treatments</Button></Link>
                    </div>
                  ) : (
                    savedClinics.map((item) => {
                      const clinic = item.clinic || item;
                      const clinicId = item.clinicId || item.id;
                      return (
                        <div key={clinicId} className="border rounded-xl p-4 flex items-center gap-4">
                          <img
                            src={getClinicImage(clinic)}
                            alt={clinic.name}
                            className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=200'; }}
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{clinic.name}</h3>
                            <p className="text-sm text-gray-500">{clinic.city}</p>
                            {clinic.rating > 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <Star className="h-3.5 w-3.5 text-[#F5A623] fill-[#F5A623]" />
                                <span className="text-xs text-gray-600">{parseFloat(clinic.rating).toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Link to={`/clinic/${clinic.id || clinicId}`}>
                              <Button size="sm" variant="outline" className="text-[#1FA7A1] border-[#1FA7A1]">View</Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnsaveClinic(clinicId)}
                              className="text-red-400 hover:text-red-600"
                              data-testid={`unsave-${clinicId}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="space-y-4" data-testid="messages-tab">
                  <h2 className="font-bold text-gray-900 mb-4">Messages</h2>
                  <div className="text-center py-12">
                    <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No messages yet</p>
                    <p className="text-sm text-gray-400 mt-1">Messages from clinics will appear here</p>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6" data-testid="settings-tab">
                  <h2 className="font-bold text-gray-900">Profile Settings</h2>

                  {/* Avatar Section */}
                  <div className="bg-gray-50 rounded-2xl p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 text-sm">Profile Photo</h3>
                    <div className="flex items-center gap-4">
                      {/* Avatar display */}
                      {user?.avatar || user?.avatarUrl ? (
                        <img
                          src={user.avatar || user.avatarUrl}
                          alt={user.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-[#1FA7A1]/20 flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          data-testid="avatar-preview"
                        />
                      ) : (
                        <div
                          className="w-16 h-16 rounded-full bg-[#1FA7A1]/10 flex items-center justify-center border-2 border-[#1FA7A1]/20 flex-shrink-0"
                          data-testid="avatar-placeholder"
                        >
                          <span className="text-xl font-bold text-[#1FA7A1]">
                            {(user?.name || user?.email || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      {/* Upload / Remove actions */}
                      <div className="flex flex-wrap gap-2">
                        <input
                          ref={avatarFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAvatarUpload}
                          data-testid="avatar-file-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => avatarFileInputRef.current?.click()}
                          disabled={avatarUploading}
                          data-testid="avatar-upload-btn"
                          className="text-[#1FA7A1] border-[#1FA7A1]/30 hover:bg-[#1FA7A1]/5 text-sm"
                        >
                          {avatarUploading ? (
                            <span className="flex items-center gap-1.5">
                              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Uploading…
                            </span>
                          ) : (
                            <><Camera className="h-3.5 w-3.5 mr-1.5" /> Change Photo</>
                          )}
                        </Button>
                        {(user?.avatar || user?.avatarUrl) && !avatarUploading && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleAvatarDelete}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 text-sm"
                            data-testid="avatar-delete-btn"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Horizontal divider */}
                  <hr className="border-gray-100" />

                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <Input
                          type="text"
                          placeholder="Enter your name"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="h-11"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="h-11"
                        />
                      </div>
                      {user.phone && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                          <Input type="tel" value={user.phone} disabled className="h-11 bg-gray-50" />
                          <p className="text-xs text-gray-400 mt-1">Phone number cannot be changed</p>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <Button onClick={handleSaveProfile} className="bg-[#1FA7A1] hover:bg-[#178f8a]">
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setIsEditing(false)}>
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="border rounded-xl p-4">
                        <p className="text-sm text-gray-500">Full Name</p>
                        <p className="font-medium text-gray-900">{user.name || 'Not set'}</p>
                      </div>
                      <div className="border rounded-xl p-4">
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-900">{user.email || 'Not set'}</p>
                      </div>
                      {user.phone && (
                        <div className="border rounded-xl p-4">
                          <p className="text-sm text-gray-500">Phone Number</p>
                          <p className="font-medium text-gray-900">{user.phone}</p>
                        </div>
                      )}
                      <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
                    </div>
                  )}

                  <hr />

                  {/* Change Password */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Change Password</h3>
                    {isChangingPassword ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                          <Input
                            type="password"
                            placeholder="Enter current password"
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className="h-11"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                          <Input
                            type="password"
                            placeholder="Enter new password"
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className="h-11"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                          <Input
                            type="password"
                            placeholder="Confirm new password"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            className="h-11"
                          />
                        </div>
                        <div className="flex gap-3">
                          <Button onClick={handleChangePassword} className="bg-[#1FA7A1] hover:bg-[#178f8a]">
                            <Lock className="h-4 w-4 mr-2" />
                            Update Password
                          </Button>
                          <Button variant="outline" onClick={() => setIsChangingPassword(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                        <Lock className="h-4 w-4 mr-2" />
                        Change Password
                      </Button>
                    )}
                  </div>

                  <hr />

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Account Actions</h3>
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="review-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-1">Review {reviewModal.clinicName}</h3>
            <p className="text-sm text-gray-500 mb-4">Share your experience</p>
            {/* Star rating */}
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} onClick={() => setReviewRating(star)} data-testid={`review-star-${star}`}>
                  <Star className={`h-7 w-7 transition-colors ${star <= reviewRating ? 'text-[#F5A623] fill-[#F5A623]' : 'text-gray-300'}`} />
                </button>
              ))}
              <span className="text-sm text-gray-500 ml-2 self-center">{reviewRating}/5</span>
            </div>
            {/* Comment textarea */}
            <textarea
              className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]"
              rows={4}
              placeholder="Tell others about your experience..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              data-testid="review-comment-input"
            />
            {/* Actions */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setReviewModal(null); setReviewText(''); setReviewRating(5); }}
                className="flex-1 border border-gray-200 rounded-xl py-2 text-sm text-gray-600 hover:bg-gray-50"
                data-testid="review-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={reviewSubmitting}
                className="flex-1 bg-[#1FA7A1] text-white rounded-xl py-2 text-sm font-medium hover:bg-[#178a85] disabled:opacity-50"
                data-testid="review-submit-btn"
              >
                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PatientProfilePage;
